import React, { useEffect, useMemo, useState } from 'react';
import supabase from '../../lib/supabase';

const TEAM_OPTIONS = ['Senior', 'K17', 'K14', 'K12', 'K10', 'K6'];

const NewsRelationsEditor = ({
  selectedPlayerIds,
  setSelectedPlayerIds,
  selectedMatchIds,
  setSelectedMatchIds,
  teamCategories,
  setTeamCategories,
}) => {
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [playersResult, matchesResult] = await Promise.all([
        supabase
          .from('team_members_aox1234')
          .select('id,name,position,team_categories,is_active,is_published,is_staff')
          .eq('is_staff', false)
          .eq('is_published', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('matches_aox1234')
          .select('id,date,time,home_team,away_team,team_category,round_label')
          .order('date', { ascending: false })
          .order('time', { ascending: false }),
      ]);
      if (cancelled) return;
      setPlayers(playersResult.data || []);
      setMatches(matchesResult.data || []);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const filteredPlayers = useMemo(() => {
    const q = playerSearch.trim().toLocaleLowerCase('el');
    if (!q) return players;
    return players.filter((player) => `${player.name || ''} ${player.position || ''}`.toLocaleLowerCase('el').includes(q));
  }, [players, playerSearch]);

  const toggle = (value, current, setter) => {
    setter(current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  };

  if (loading) return <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">Φόρτωση συνδέσεων...</div>;

  return (
    <div className="rounded-2xl border border-primary-100 bg-primary-50/40 p-5 space-y-5">
      <div>
        <h3 className="font-black text-gray-900">Συνδέσεις περιεχομένου</h3>
        <p className="mt-1 text-sm text-gray-600">Σύνδεσε την είδηση με ομάδα, παίκτριες και αγώνες. Οι συνδέσεις εμφανίζονται αυτόματα στα σχετικά προφίλ και Match Center.</p>
      </div>

      <div>
        <div className="mb-2 text-sm font-bold text-gray-700">Ομάδες / τμήματα</div>
        <div className="flex flex-wrap gap-2">
          {TEAM_OPTIONS.map((team) => {
            const active = teamCategories.includes(team);
            return (
              <button key={team} type="button" onClick={() => toggle(team, teamCategories, setTeamCategories)} className={`rounded-full border px-3 py-1.5 text-sm font-bold transition-colors ${active ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300'}`}>
                {team}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="text-sm font-bold text-gray-700">Παίκτριες <span className="font-normal text-gray-500">({selectedPlayerIds.length})</span></div>
          <input value={playerSearch} onChange={(e) => setPlayerSearch(e.target.value)} placeholder="Αναζήτηση παίκτριας..." className="w-56 max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
        </div>
        <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
          {filteredPlayers.map((player) => (
            <label key={player.id} className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-50">
              <input type="checkbox" checked={selectedPlayerIds.includes(player.id)} onChange={() => toggle(player.id, selectedPlayerIds, setSelectedPlayerIds)} className="h-4 w-4" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900">{player.name}</div>
                <div className="text-xs text-gray-500">{player.position || 'Παίκτρια'}{Array.isArray(player.team_categories) && player.team_categories.length ? ` • ${player.team_categories.join(', ')}` : ''}</div>
              </div>
            </label>
          ))}
          {filteredPlayers.length === 0 && <div className="px-4 py-5 text-sm text-gray-500">Δεν βρέθηκαν παίκτριες.</div>}
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-bold text-gray-700">Αγώνες <span className="font-normal text-gray-500">({selectedMatchIds.length})</span></div>
        <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
          {matches.map((match) => (
            <label key={match.id} className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-50">
              <input type="checkbox" checked={selectedMatchIds.includes(match.id)} onChange={() => toggle(match.id, selectedMatchIds, setSelectedMatchIds)} className="h-4 w-4" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900">{match.home_team} – {match.away_team}</div>
                <div className="text-xs text-gray-500">{match.date ? new Date(`${String(match.date).slice(0, 10)}T12:00:00`).toLocaleDateString('el-GR') : ''}{match.time ? ` • ${String(match.time).slice(0, 5)}` : ''}{match.team_category ? ` • ${match.team_category}` : ''}{match.round_label ? ` • ${match.round_label}` : ''}</div>
              </div>
            </label>
          ))}
          {matches.length === 0 && <div className="px-4 py-5 text-sm text-gray-500">Δεν υπάρχουν καταχωρημένοι αγώνες.</div>}
        </div>
      </div>
    </div>
  );
};

export default NewsRelationsEditor;
